import { useRef, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { translateGroupName } from '@pantry/i18n';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@pantry/ui';
import { logger } from '../../lib/logger';
import {
  useCreateItem,
  useCreateItemInstance,
  useUpdateItem,
  useCreateProduct,
  useRemoveGroupMember,
  useDismissAutoGroup,
  materializeExternalProduct,
  queryKeys,
  type ItemWithRelations,
  type CreateItemData,
} from '@pantry/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { looksLikeBarcode, formatDate } from '@pantry/shared';
import type { CreateItemFormValues, EditItemFormValues } from '@pantry/shared';
import { UnifiedItemForm } from './unified-form';

interface ItemFormDialogProps {
  householdId: string;
  item?: ItemWithRelations;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose: () => void;
  onSuccess?: () => void;
  /** Initial product search term to pre-fill (used by omni-bar) */
  defaultProductSearch?: string;
}

export function ItemFormDialog({
  householdId,
  item,
  open = true,
  onOpenChange,
  onClose,
  onSuccess,
  defaultProductSearch,
}: ItemFormDialogProps) {
  const { t, i18n } = useLingui();
  const isEditing = !!item;

  const queryClient = useQueryClient();
  const createItem = useCreateItem();
  const createInstance = useCreateItemInstance();
  const updateItem = useUpdateItem();
  const createProduct = useCreateProduct();
  const removeGroupMember = useRemoveGroupMember();
  const dismissAutoGroup = useDismissAutoGroup();

  const undoTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [isMaterializing, setIsMaterializing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPending =
    createItem.isPending ||
    createInstance.isPending ||
    updateItem.isPending ||
    createProduct.isPending ||
    isMaterializing;

  const handleCreateSubmit = async (values: CreateItemFormValues) => {
    setError(null);

    try {
      let productId: string | undefined;

      if (values.selectedProduct?.type === 'external') {
        // Materialize external product from Open Food Facts
        setIsMaterializing(true);
        try {
          const materialized = await materializeExternalProduct(
            values.selectedProduct.product.barcode
          );
          productId = materialized.id;
        } catch (err) {
          logger.error('Failed to materialize external product', err, {
            barcode: values.selectedProduct.product.barcode,
          });
          setError(t`Failed to save product from Open Food Facts`);
          setIsMaterializing(false);
          return;
        }
        setIsMaterializing(false);
      } else if (values.selectedProduct?.type === 'local') {
        productId = values.selectedProduct.product.id;
      } else if (values.barcode && values.customName.trim()) {
        // Custom name with barcode - find or create product
        if (!looksLikeBarcode(values.barcode)) {
          setError(t`Invalid barcode format. Must be 8-14 digits.`);
          return;
        }

        setIsMaterializing(true);
        try {
          const existing = await materializeExternalProduct(values.barcode);
          productId = existing.id;
        } catch {
          // Not found externally - create new user product
          try {
            const newProduct = await createProduct.mutateAsync({
              name: values.customName.trim(),
              barcode: values.barcode,
            });
            productId = newProduct.id;
          } catch (createErr) {
            logger.error('Failed to create product', createErr, {
              barcode: values.barcode,
            });
            setError(t`Failed to create product with barcode`);
            setIsMaterializing(false);
            return;
          }
        }
        setIsMaterializing(false);
      }

      // Only save customName if different from product's original name
      const originalProductName = values.selectedProduct?.product?.name || '';
      const trimmedCustomName = values.customName.trim();
      const hasCustomName = trimmedCustomName && trimmedCustomName !== originalProductName;

      const itemData: CreateItemData = {
        productId,
        storageLocationId: values.storageLocationId || undefined,
        customName: hasCustomName ? trimmedCustomName : undefined,
        quantity: values.quantity,
        unit: values.unit,
        notes: values.notes,
        createdFrom: 'web' as const,
      };

      const result = await createItem.mutateAsync({
        householdId,
        data: itemData,
      });

      // Always create an instance - expiry date is optional
      await createInstance.mutateAsync({
        householdId,
        itemId: result.id,
        data: {
          quantity: values.quantity,
          purchaseDate: values.purchaseDate || formatDate(new Date()),
          expiryDate: values.expiryDate,
        },
      });

      // Re-invalidate to get fresh activeQuantity including new instance
      await queryClient.invalidateQueries({
        queryKey: queryKeys.items.byHousehold(householdId),
      });

      // Show auto-group toast with undo if the item was auto-grouped
      if (result.autoGrouped) {
        const { groupId, groupName } = result.autoGrouped;
        const translatedGroupName = translateGroupName(i18n, groupName);
        const productName = values.selectedProduct?.product?.name || values.customName || t`Item`;

        toast(t`"${productName}" added to ${translatedGroupName} group`, {
          duration: 5000,
          action: {
            label: t`Undo`,
            onClick: () => {
              clearTimeout(undoTimerRef.current);
              removeGroupMember.mutate(
                { householdId, groupId, productId: result.productId! },
                {
                  onSuccess: () => {
                    dismissAutoGroup.mutate({
                      householdId,
                      groupId,
                      productId: result.productId!,
                    });
                  },
                }
              );
            },
          },
        });
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      logger.error('Failed to create item', err, { householdId });
      setError(err instanceof Error ? err.message : t`Failed to save item`);
    }
  };

  const handleEditSubmit = async (values: EditItemFormValues) => {
    setError(null);

    if (!item) return;

    try {
      const originalProductName = values.selectedProduct?.product?.name || '';
      const trimmedCustomName = values.customName.trim();
      const hasCustomName = trimmedCustomName && trimmedCustomName !== originalProductName;

      await updateItem.mutateAsync({
        householdId,
        id: item.id,
        data: {
          productId:
            values.selectedProduct?.type === 'local'
              ? values.selectedProduct.product.id
              : item.productId || undefined,
          storageLocationId: values.storageLocationId || undefined,
          customName: hasCustomName ? trimmedCustomName : null,
          quantity: values.quantity,
          unit: values.unit,
          notes: values.notes,
        },
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      logger.error('Failed to update item', err, {
        householdId,
        itemId: item.id,
      });
      setError(err instanceof Error ? err.message : t`Failed to save item`);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange?.(newOpen);
    if (!newOpen) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-tour="inventory-item-form"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {isEditing ? <Trans>Edit Item</Trans> : <Trans>Add New Item</Trans>}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? (
              <Trans>Update the details for this item in your inventory.</Trans>
            ) : (
              <Trans>Search for a product or enter a custom name to add to your inventory.</Trans>
            )}
          </DialogDescription>
        </DialogHeader>

        {isEditing ? (
          <UnifiedItemForm
            mode="edit-item"
            householdId={householdId}
            item={item}
            isPending={isPending}
            error={error}
            onSubmit={handleEditSubmit}
            onCancel={onClose}
          />
        ) : (
          <UnifiedItemForm
            mode="create-item"
            householdId={householdId}
            defaultProductSearch={defaultProductSearch}
            isPending={isPending}
            error={error}
            onSubmit={handleCreateSubmit}
            onCancel={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
